/*
 * A guide's or editorial FAQ's sources: the laws, decrees and official pages
 * it is based on (the optional `sources` field of seeds/legal-magazine and
 * seeds/editorial-faq entries, stored on the Blog or Question document).
 * Shared by the seeds, scripts/publish-guides.js and publish-faq.js, the page
 * renderer and the UI (through the `@seo` alias), so all of them accept and
 * show exactly the same entries.
 *
 *   { title, url, publisher?, lang? }
 *
 * Only http(s) URLs are kept. Everything returned is plain text; callers
 * escape it for HTML.
 */
const MAX_SOURCES = 10;
const MAX_LENGTH = { title: 300, url: 2000, publisher: 160 };

const text = (value) => (typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "");

/** The URL, trimmed, when it is an absolute http(s) URL; otherwise "". */
function httpUrl(value) {
  const url = typeof value === "string" ? value.trim() : "";
  if (!url || url.length > MAX_LENGTH.url || !/^https?:\/\//i.test(url)) return "";
  // No whitespace, control characters or angle brackets inside a URL.
  // eslint-disable-next-line no-control-regex
  if (/[\s<>\u0000-\u001f\u007f]/.test(url)) return "";
  try {
    const parsed = new URL(url);
    if ((parsed.protocol !== "http:" && parsed.protocol !== "https:") || !parsed.hostname) return "";
  } catch (err) {
    return "";
  }
  return url;
}

const languageTag = (value) => {
  const tag = text(value);
  return /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/i.test(tag) ? tag : "";
};

/**
 * Validate a list of sources: { sources, problems }. Invalid entries are
 * dropped (problems says which and why), duplicates by URL too, and at most
 * MAX_SOURCES are kept. Never throws.
 */
function checkSources(list) {
  const problems = [];
  if (list == null) return { sources: [], problems };
  if (!Array.isArray(list)) return { sources: [], problems: ["sources is not an array"] };
  const sources = [];
  const seen = new Set();
  list.forEach((item, i) => {
    const where = `source ${i + 1}`;
    if (!item || typeof item !== "object") {
      problems.push(`${where}: not an object`);
      return;
    }
    const title = text(item.title);
    const url = httpUrl(item.url);
    const publisher = text(item.publisher);
    if (!title) problems.push(`${where}: no title`);
    else if (title.length > MAX_LENGTH.title) problems.push(`${where}: title longer than ${MAX_LENGTH.title} characters`);
    if (!url) problems.push(`${where}: not an http(s) URL (${String(item.url).slice(0, 80)})`);
    if (publisher.length > MAX_LENGTH.publisher) problems.push(`${where}: publisher longer than ${MAX_LENGTH.publisher} characters`);
    if (!title || title.length > MAX_LENGTH.title || !url || publisher.length > MAX_LENGTH.publisher) return;
    if (seen.has(url)) {
      problems.push(`${where}: duplicate URL ${url}`);
      return;
    }
    seen.add(url);
    const lang = languageTag(item.lang);
    if (item.lang != null && !lang) problems.push(`${where}: lang "${item.lang}" ignored`);
    sources.push({ title, url, ...(publisher ? { publisher } : {}), ...(lang ? { lang } : {}) });
  });
  if (sources.length > MAX_SOURCES) {
    problems.push(`${sources.length} sources: only the first ${MAX_SOURCES} are kept`);
    sources.length = MAX_SOURCES;
  }
  return { sources, problems };
}

/** The valid sources of a list (see checkSources). */
function cleanSources(list) {
  return checkSources(list).sources;
}

/** schema.org `citation` for an Article: CreativeWork { name, url } per source. */
function citationLd(sources) {
  return cleanSources(sources).map((s) => ({ "@type": "CreativeWork", name: s.title, url: s.url }));
}

/**
 * The date an article was last updated: the latest of its content update
 * (updatedAt), its last review (lastReviewedAt) and its publication
 * (createdAt), so it is never earlier than the date it was published.
 */
function lastUpdated(doc) {
  const times = [doc && doc.updatedAt, doc && doc.lastReviewedAt, doc && doc.createdAt]
    .map((d) => (d ? new Date(d).getTime() : NaN))
    .filter((t) => !Number.isNaN(t));
  return times.length ? new Date(Math.max(...times)) : null;
}

module.exports = { MAX_SOURCES, checkSources, cleanSources, citationLd, lastUpdated, httpUrl };
